import React from 'react';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, ImageRun, AlignmentType, BorderStyle, WidthType } from 'docx';
import { OrganisationSettings } from '../../services/organisationSettingsService';

interface ExecutiveChatExportProps {
  messageContent: string; // The AI's response content
  userQuestion: string;
  messageElement: HTMLElement; // The DOM element containing the rendered message
  organizationSettings?: OrganisationSettings | null;
}

/**
 * Export Executive Chat message to Word document
 * Parses rendered components (KPI cards, charts, tables, highlights) and generates .docx
 */
export async function exportExecutiveChatToWord({
  messageContent,
  userQuestion,
  messageElement,
  organizationSettings
}: ExecutiveChatExportProps): Promise<void> {
  try {
    console.log('📄 Starting Executive Chat Word export...');
    
    const currentDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    // Organization settings doesn't include name, default to 'Lumina AI'
    const organizationName = 'Lumina AI';
    
    // Generate title from user question
    const generateTitle = (question: string): string => {
      const q = question.toLowerCase();
      if (q.includes('top') || q.includes('best') || q.includes('highest')) {
        if (q.includes('dealer') || q.includes('location')) return 'Top Performance Analysis';
        if (q.includes('revenue')) return 'Revenue Performance Analysis';
        return 'Performance Rankings Analysis';
      }
      if (q.includes('compare') || q.includes('comparison')) return 'Comparative Analysis';
      if (q.includes('trend')) return 'Trend Analysis';
      if (q.includes('revenue')) return 'Revenue Analysis';
      if (q.includes('profit')) return 'Profitability Analysis';
      
      return 'Executive Analysis Report';
    };
    
    const reportTitle = generateTitle(userQuestion);
    
    // Prepare document children array
    const children: any[] = [];
    
    // Add title page
    children.push(
      new Paragraph({
        text: reportTitle,
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { before: 400, after: 200 }
      }),
      new Paragraph({
        text: organizationName,
        heading: HeadingLevel.HEADING_2,
        alignment: AlignmentType.CENTER,
        spacing: { before: 0, after: 400 }
      }),
      new Paragraph({
        text: `Report Date: ${currentDate}`,
        alignment: AlignmentType.CENTER,
        spacing: { before: 0, after: 120 }
      }),
      new Paragraph({
        text: `Question: ${userQuestion}`,
        alignment: AlignmentType.CENTER,
        spacing: { before: 0, after: 120 }
      }),
      new Paragraph({
        text: '---',
        alignment: AlignmentType.CENTER,
        spacing: { before: 200, after: 200 }
      }),
      new Paragraph({
        text: 'Generated by Lumina AI Executive Chat',
        alignment: AlignmentType.CENTER,
        spacing: { before: 0, after: 120 }
      }),
      // Page break after title page
      new Paragraph({
        text: '',
        pageBreakBefore: true
      })
    );
    
    const timelineBlocks = Array.from(messageElement.querySelectorAll('.ec-timeline-text-block'));

    if (timelineBlocks.length > 0) {
      // NEW FORMAT: Process timeline blocks in DOM order
      console.log('📋 Processing timeline-based message for Word export');
      for (const block of timelineBlocks) {
        // Process all elements within this block in DOM order
        await processBlockElements(block as HTMLElement, children);
      }
    } else {
      // OLD FORMAT: Process elements by type (backwards compatibility)
      console.log('📋 Processing legacy message format for Word export');
      await processLegacyElements(messageElement, children);
    }
    
    // Create and download document
    const doc = new Document({
      sections: [{
        properties: {},
        children: children
      }]
    });
    
    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sanitizeFilename(userQuestion)}.docx`;
    link.click();
    URL.revokeObjectURL(url);
    
    console.log('✅ Word document generated successfully');
  } catch (error) {
    console.error('❌ Error generating Word document:', error);
    throw error;
  }
}

// Process a single timeline block's elements in order
async function processBlockElements(block: HTMLElement, children: any[]) {
  const elements = Array.from(block.children);
  
  for (const element of elements) {
    // KPI Cards Grid
    if (element.classList.contains('exec-kpi-grid')) {
      await processKPICards(element, children);
    }
    // Charts
    else if (element.classList.contains('exec-chart-card')) {
      await processChart(element, children);
    }
    // Highlight boxes
    else if (element.classList.contains('exec-highlight-box')) {
      processHighlightBox(element, children);
    }
    // Markdown text (tables, paragraphs, etc.)
    else if (element.classList.contains('exec-markdown-text')) {
      await processMarkdownSection(element, children);
    }
  }
}

// Process KPI Cards
async function processKPICards(grid: Element, children: any[]) {
  const cards = grid.querySelectorAll('.exec-kpi-card');
  
  if (cards.length > 0) {
    children.push(
      new Paragraph({
        text: 'Key Performance Indicators',
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 240, after: 120 }
      })
    );
    
    // Create table for KPI cards (2 columns)
    const kpiRows: TableRow[] = [];
    for (let i = 0; i < cards.length; i += 2) {
      const card1 = cards[i];
      const card2 = cards[i + 1];
          
          const cell1Content: Paragraph[] = [];
          const label1 = card1.querySelector('.exec-kpi-label')?.textContent || '';
          const value1 = card1.querySelector('.exec-kpi-value')?.textContent || '';
          const desc1 = card1.querySelector('.exec-kpi-desc')?.textContent || '';
          const trend1 = card1.querySelector('.exec-kpi-change')?.textContent || '';
          
          cell1Content.push(
            new Paragraph({
              children: [
                new TextRun({ text: label1, bold: true, size: 20 })
              ],
              spacing: { after: 60 }
            }),
            new Paragraph({
              children: [
                new TextRun({ text: value1, size: 28, bold: true })
              ],
              spacing: { after: 60 }
            })
          );
          
          if (trend1) {
            cell1Content.push(
              new Paragraph({
                children: [
                  new TextRun({ text: trend1, size: 18 })
                ],
                spacing: { after: 60 }
              })
            );
          }
          
          if (desc1) {
            cell1Content.push(
              new Paragraph({
                text: desc1,
                spacing: { after: 60 }
              })
            );
          }
          
          const cells: TableCell[] = [
            new TableCell({
              children: cell1Content,
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" }
              },
              width: { size: 50, type: WidthType.PERCENTAGE }
            })
          ];
          
          if (card2) {
            const cell2Content: Paragraph[] = [];
            const label2 = card2.querySelector('.exec-kpi-label')?.textContent || '';
            const value2 = card2.querySelector('.exec-kpi-value')?.textContent || '';
            const desc2 = card2.querySelector('.exec-kpi-desc')?.textContent || '';
            const trend2 = card2.querySelector('.exec-kpi-change')?.textContent || '';
            
            cell2Content.push(
              new Paragraph({
                children: [
                  new TextRun({ text: label2, bold: true, size: 20 })
                ],
                spacing: { after: 60 }
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: value2, size: 28, bold: true })
                ],
                spacing: { after: 60 }
              })
            );
            
            if (trend2) {
              cell2Content.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: trend2, size: 18 })
                  ],
                  spacing: { after: 60 }
                })
              );
            }
            
            if (desc2) {
              cell2Content.push(
                new Paragraph({
                  text: desc2,
                  spacing: { after: 60 }
                })
              );
            }
            
            cells.push(
              new TableCell({
                children: cell2Content,
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
                  bottom: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
                  left: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
                  right: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" }
                },
                width: { size: 50, type: WidthType.PERCENTAGE }
              })
            );
          } else {
            // Add empty cell if odd number of cards
            cells.push(
              new TableCell({
                children: [new Paragraph({ text: '' })],
                borders: {
                  top: { style: BorderStyle.NONE },
                  bottom: { style: BorderStyle.NONE },
                  left: { style: BorderStyle.NONE },
                  right: { style: BorderStyle.NONE }
                },
                width: { size: 50, type: WidthType.PERCENTAGE }
              })
            );
          }
          
          kpiRows.push(new TableRow({ children: cells }));
        }
        
    children.push(
      new Table({
        rows: kpiRows,
        width: {
          size: 100,
          type: WidthType.PERCENTAGE
        }
      }),
      new Paragraph({
        text: "",
        spacing: { before: 0, after: 200 }
      })
    );
  }
}

// Process a single chart
async function processChart(chartCard: Element, children: any[]) {
  const titleElement = chartCard.querySelector('.exec-chart-title');
  const canvas = chartCard.querySelector('canvas') as HTMLCanvasElement;
  
  if (titleElement?.textContent) {
    children.push(
      new Paragraph({
        text: titleElement.textContent.trim(),
        heading: HeadingLevel.HEADING_3,
        spacing: { before: 200, after: 120 }
      })
    );
  }
  
  if (canvas) {
    try {
      // Capture chart as image
      const dataUrl = await captureChartImage(canvas);
      
      if (dataUrl) {
        // Calculate aspect ratio to maintain proportions
        const canvasWidth = canvas.width || 600;
        const canvasHeight = canvas.height || 400;
        const aspectRatio = canvasWidth / canvasHeight;
        
        // Target width for Word document
        const targetWidth = 500;
        const targetHeight = Math.round(targetWidth / aspectRatio);
        
        console.log(`📊 Chart dimensions: ${canvasWidth}x${canvasHeight}, aspect ratio: ${aspectRatio.toFixed(2)}, Word: ${targetWidth}x${targetHeight}`);
        
        children.push(
          new Paragraph({
            children: [
              new ImageRun({
                data: dataUrl.split(',')[1],
                transformation: {
                  width: targetWidth,
                  height: targetHeight
                },
                type: 'png'
              })
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 200 }
          })
        );
        console.log('✅ Chart image captured successfully with maintained aspect ratio');
      }
    } catch (err) {
      console.error('Error capturing chart:', err);
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: '[Chart visualization not available]',
              italics: true
            })
          ],
          spacing: { before: 0, after: 200 }
        })
      );
    }
  }
}

// Process highlight box
function processHighlightBox(highlight: Element, children: any[]) {
  const type = highlight.classList.contains('exec-highlight-success') ? 'success' :
               highlight.classList.contains('exec-highlight-warning') ? 'warning' :
               highlight.classList.contains('exec-highlight-info') ? 'info' : 'default';
  
  const textContent = highlight.textContent?.trim() || '';
  
  // Use different colors based on type
  const shading = 
    type === 'success' ? 'D4EDDA' :
    type === 'warning' ? 'FFF3CD' :
    type === 'info' ? 'D1ECF1' : 'F8F9FA';
  
  children.push(
    new Paragraph({
      text: textContent,
      spacing: { before: 120, after: 120 },
      shading: {
        type: 'clear' as any,
        fill: shading
      },
      border: {
        top: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
        bottom: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
        left: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
        right: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" }
      }
    })
  );
}

// Process markdown section (tables, paragraphs, etc.)
async function processMarkdownSection(markdownElement: Element, children: any[]) {
  // Process tables
  const tables = markdownElement.querySelectorAll('table');
  for (const table of Array.from(tables)) {
      const rows = Array.from(table.querySelectorAll('tr'));
      const tableRows = rows.map(row => {
        const cells = Array.from(row.querySelectorAll('th, td'));
        return new TableRow({
          children: cells.map(cell => 
            new TableCell({
              children: [
                new Paragraph({
                  text: cell.textContent?.trim() || '',
                  spacing: { before: 60, after: 60 }
                })
              ],
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "000000" }
              }
            })
          )
        });
      });
      
    children.push(
      new Table({
        rows: tableRows,
        width: {
          size: 100,
          type: WidthType.PERCENTAGE
        }
      }),
      new Paragraph({
        text: "",
        spacing: { before: 0, after: 200 }
      })
    );
  }
  
  // Process Regular Markdown Text (paragraphs, headings, lists) - skip tables already processed
  const markdownElements = Array.from(markdownElement.querySelectorAll(':scope > *'));
  for (const element of markdownElements) {
      if (element instanceof HTMLElement) {
        const tagName = element.tagName.toLowerCase();
        const textContent = element.textContent?.trim() || '';
        
        // Skip if already processed (tables)
        if (tagName === 'table') continue;
        if (!textContent) continue;
        
        // Process headings
        if (/^h[1-4]$/.test(tagName)) {
          const level = parseInt(tagName.charAt(1));
          const headingLevel = level === 1 ? HeadingLevel.HEADING_1 :
                             level === 2 ? HeadingLevel.HEADING_2 :
                             level === 3 ? HeadingLevel.HEADING_3 :
                             HeadingLevel.HEADING_4;
          
          children.push(
            new Paragraph({
              text: textContent,
              heading: headingLevel,
              spacing: { before: 240, after: 120 }
            })
          );
        }
        // Process paragraphs
        else if (tagName === 'p') {
          children.push(
            new Paragraph({
              text: textContent,
              spacing: { before: 0, after: 120 }
            })
          );
        }
        // Process lists
        else if (tagName === 'ul' || tagName === 'ol') {
          const items = Array.from(element.querySelectorAll('li'));
          
          items.forEach((item, index) => {
            children.push(
              new Paragraph({
                text: `• ${item.textContent?.trim() || ''}`,
                spacing: { before: 0, after: index === items.length - 1 ? 200 : 60 }
              })
            );
          });
        }
      }
    }
}

// Legacy format processor (for backwards compatibility)
async function processLegacyElements(messageElement: HTMLElement, children: any[]) {
  // Process KPI Cards
  const kpiGrids = messageElement.querySelectorAll('.exec-kpi-grid');
  for (const grid of Array.from(kpiGrids)) {
    await processKPICards(grid, children);
  }
  
  // Process Charts
  const chartCards = messageElement.querySelectorAll('.exec-chart-card');
  for (const chartCard of Array.from(chartCards)) {
    await processChart(chartCard, children);
  }
  
  // Process Tables
  const tables = messageElement.querySelectorAll('.exec-markdown-text table');
  for (const table of Array.from(tables)) {
    const rows = Array.from(table.querySelectorAll('tr'));
    const tableRows = rows.map(row => {
      const cells = Array.from(row.querySelectorAll('th, td'));
      return new TableRow({
        children: cells.map(cell => 
          new TableCell({
            children: [
              new Paragraph({
                text: cell.textContent?.trim() || '',
                spacing: { before: 60, after: 60 }
              })
            ],
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
              right: { style: BorderStyle.SINGLE, size: 1, color: "000000" }
            }
          })
        )
      });
    });
    
    children.push(
      new Table({
        rows: tableRows,
        width: {
          size: 100,
          type: WidthType.PERCENTAGE
        }
      }),
      new Paragraph({
        text: "",
        spacing: { before: 0, after: 200 }
      })
    );
  }
  
  // Process Highlight Boxes
  const highlights = messageElement.querySelectorAll('.exec-highlight-box');
  for (const highlight of Array.from(highlights)) {
    processHighlightBox(highlight, children);
  }
  
  // Process Regular Markdown
  const markdownSections = messageElement.querySelectorAll('.exec-markdown-text');
  for (const section of Array.from(markdownSections)) {
    await processMarkdownSection(section, children);
  }
}

/**
 * Sanitize filename for safe download
 */
function sanitizeFilename(filename: string): string {
  return filename
    .replace(/[^a-z0-9]/gi, '_')
    .slice(0, 50) // Limit length
    .replace(/_+/g, '_') // Remove consecutive underscores
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
}

/**
 * Capture Chart.js canvas as base64 image
 */
async function captureChartImage(canvas: HTMLCanvasElement): Promise<string | null> {
  try {
    // Ensure canvas has dimensions
    if (canvas.width === 0 || canvas.height === 0) {
      console.log('Canvas has zero dimensions, setting default size');
      canvas.width = 600;
      canvas.height = 400;
    }
    
    console.log(`Canvas dimensions: ${canvas.width}x${canvas.height}`);
    
    // Get context and add white background
    const ctx = canvas.getContext('2d');
    if (ctx) {
      // Store original image data
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      // Draw white background
      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
      
      // Capture with white background
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      
      // Restore original image data
      ctx.putImageData(imgData, 0, 0);
      
      if (dataUrl && dataUrl.length > 100) {
        return dataUrl;
      }
    }
    
    // Fallback: simple toDataURL
    const simpleDataUrl = canvas.toDataURL('image/png', 1.0);
    if (simpleDataUrl && simpleDataUrl.length > 100) {
      return simpleDataUrl;
    }
    
    return null;
  } catch (err) {
    console.error('Chart capture failed:', err);
    return null;
  }
}

